import React, { useContext, useEffect, useState } from 'react';
import { getAxiosTokenInstance } from '../../../utils/axiosInstance';
import { NecttosContext } from '../../../context/NecttosContext';

interface AddGroupProps {
  students: string[];
  subjectId: string;
  modules: any[];
}

interface Group {
  groupName: string;
  studentIds: string[];
}

interface ModuleGroups {
  moduleId: string;
  groups: Group[];
}

const AddGroup: React.FC<AddGroupProps> = ({ students, modules, subjectId }) => {
  const { collegeId } = useContext(NecttosContext);
  const [loading, setLoading] = useState(false);
  const [theStudents, setTheStudents] = useState<{ _id: string; name: string }[]>([]);
  const [formGroups, setFormGroups] = useState<Group[]>([]);
  const [globalModuleId, setGlobalModuleId] = useState('');
  const [existingGroups, setExistingGroups] = useState<ModuleGroups[]>([]);

  const fetchStudentNames = async () => {
    try {
      const instance = await getAxiosTokenInstance();
      const resp = await instance.post('/college/lms/all/getStudentNames', { ids: students });
      if (resp?.data?.data) setTheStudents(resp.data.data);
    } catch (err) {
      console.error('Failed to fetch student names:', err);
    }
  };

  const fetchExistingGroups = async () => {
    try {
      const instance = await getAxiosTokenInstance();
      const resp = await instance.get('/college/lms/groupAssignments/getAllGroups', {
        params: { subjectId, collegeId },
      });
      if (resp?.data?.data) setExistingGroups(resp.data.data);
    } catch (err) {
      console.error('Failed to fetch existing groups:', err);
    }
  };

  useEffect(() => {
    if (students.length > 0) fetchStudentNames();
    if (collegeId && subjectId) fetchExistingGroups();
  }, [students, collegeId, subjectId]);

  const addNewGroup = () => {
    const nextGroupNum = formGroups.length + 1;
    setFormGroups([...formGroups, { groupName: `Group ${nextGroupNum}`, studentIds: [] }]);
  };

  const handleCheckboxChange = (groupIndex: number, studentId: string) => {
    setFormGroups((prev) =>
      prev.map((group, i) =>
        i === groupIndex
          ? {
              ...group,
              studentIds: group.studentIds.includes(studentId)
                ? group.studentIds.filter((id) => id !== studentId)
                : [...group.studentIds, studentId],
            }
          : group
      )
    );
  };

  const getAssignedStudents = () => {
    return formGroups.flatMap((group) => group.studentIds);
  };

  const handleSubmit = async () => {
    if (!globalModuleId) return;

    setLoading(true);
    try {
      const instance = await getAxiosTokenInstance();
      const resp = await instance.post('/college/lms/groupAssignments/createGroupforAssignment', {
        groups: formGroups,
        moduleId: "module1", // need to change to global
        subjectId: "subject1",  // need to change subjectId itself
        collegeId: "college1"   // need to change collegeId itself
      });
      if (resp?.data?.success) {
        setFormGroups([]);
        fetchExistingGroups(); // refresh after submit
      }
    } catch (err) {
      console.error('Submission error', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (moduleId: string) => {
    try {
      const instance = await getAxiosTokenInstance();
      await instance.delete('/college/lms/groupAssignments/deleteGroup', {
        params: { moduleId },
      });
      setExistingGroups(existingGroups.filter((group) => group.moduleId !== moduleId));
    } catch (err) {
      console.error('Delete error', err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="p-4 bg-white dark:bg-gray-800 rounded shadow-md border dark:border-gray-700 space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Module Selection</h3>
        <select
          value={globalModuleId}
          onChange={(e) => setGlobalModuleId(e.target.value)}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        >
          <option value="">Select a module</option>
          {modules.map((mod) => (
            <option key={mod._id} value={mod._id}>
              {mod.fullModuleName}
            </option>
          ))}
        </select>
      </div>

      {formGroups.map((group, index) => {
        const assigned = getAssignedStudents();
        const availableStudents = theStudents.filter(
          (s) => !assigned.includes(s._id) || group.studentIds.includes(s._id)
        );

        return (
          <div
            key={index}
            className="p-4 bg-white dark:bg-gray-800 rounded shadow-md border dark:border-gray-700 space-y-2"
          >
            <h4 className="font-semibold text-md text-gray-900 dark:text-white">
              {group.groupName}
            </h4>
            {availableStudents.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No students left to assign.</p>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {availableStudents.map((student) => (
                  <label key={student._id} className="flex items-center space-x-2 text-sm">
                    <input
                      type="checkbox"
                      checked={group.studentIds.includes(student._id)}
                      onChange={() => handleCheckboxChange(index, student._id)}
                      className="form-checkbox"
                    />
                    <span className="text-gray-800 dark:text-gray-200">{student.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}

      <div className="flex space-x-4">
        <button
          onClick={addNewGroup}
          className="px-4 py-2 rounded-md bg-green-600 hover:bg-green-700 text-white text-sm font-medium"
        >
          Add Another Group
        </button>

        <button
          onClick={handleSubmit}
          disabled={!globalModuleId || formGroups.length === 0 || loading}
          className={`px-4 py-2 rounded-md text-sm font-medium text-white ${
            !globalModuleId || formGroups.length === 0 || loading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {loading ? 'Submitting...' : 'Submit All Groups'}
        </button>
      </div>

      {/* Existing Groups */}
      <div className="p-4 bg-white dark:bg-gray-800 rounded shadow-md border dark:border-gray-700 space-y-2">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Existing Groups</h3>
        {existingGroups.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No groups created yet.</p>
        ) : (
          <div className="space-y-4">
            {existingGroups.map((entry, modIndex) => (
              <div key={`mod-${modIndex}`} className="space-y-3">
                <h4 className="text-md font-semibold text-blue-600 dark:text-blue-400">
                  Module ID: {entry.moduleId}
                </h4>

                {entry.groups.map((group, groupIndex) => (
                  <div
                    key={`group-${groupIndex}`}
                    className="flex items-center justify-between p-2 rounded border dark:border-gray-600"
                  >
                    <div>
                      <p className="font-medium text-gray-800 dark:text-white">{group.groupName}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {group.studentIds.length} students assigned
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        document.getElementById(`edit-${entry.moduleId}-${groupIndex}`)?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className="text-sm px-3 py-1 bg-yellow-500 hover:bg-yellow-600 text-white rounded"
                    >
                      Edit
                    </button>
                  </div>
                ))}

                <button
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                  onClick={() => handleDelete(entry.moduleId)}
                >
                  Delete Group
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddGroup;
